import { useEffect, useState } from "react";
import ChangesLog from "components/atoms/ChangesLog";
import ModerationButtons from "components/atoms/ModerationButtons";

import styles from "./style.module.scss";

import "react-quill-new/dist/quill.snow.css";
import "./style.module.scss";

import { countTextCharacters } from "@shared/utils";
import { cn } from "lib/utils";
import QuillEditor from "react-quill-new";

import { Formats, Modules } from "./constants";

type ComponentProps = {
  label?: string;
  id: string;
  disabled?: boolean;
  placeholder?: string;
  description?: string;
  value: string;
  prevValue?: string;
  required?: boolean;
  onChange: ({
    target: { id, value },
  }: {
    target: { id: string; value: string };
  }) => void;
  onChangeApprove?: (id: string, type: number) => void;
  onError?: (boolean, string) => void;
  checkErrorOnFirstRender?: boolean;
  wasChanged?: boolean;
  hasNote?: string;
  approve?: boolean;
  approved?: boolean;
  changed?: boolean;
  dismiss?: boolean;
  maxLength?: number;
  maxWords?: number;
  uncontrolledError?: any;
};

export default function InputMarkdown({
  label,
  id,
  disabled = false,
  placeholder,
  description,
  value = "",
  prevValue,
  required,
  onChange,
  onChangeApprove,
  onError,
  checkErrorOnFirstRender = false,
  wasChanged,
  hasNote,
  approve,
  approved,
  changed,
  dismiss,
  maxLength,
  maxWords,
  uncontrolledError = null,
}: ComponentProps) {
  const [checkError, setCheckError] = useState(checkErrorOnFirstRender);
  const [error, setError] = useState(
    checkErrorOnFirstRender &&
      required &&
      String(value).trim() === "" &&
      !uncontrolledError
  );

  const [showLog, setShowLog] = useState(false);

  const sanitizeMarkup = (text: string) => {
    return text?.replace(/<\/?[^>]+(>|$)/g, "");
  };

  const maxWordsCheck = maxWords
    ? sanitizeMarkup(value?.trim())?.split(/\s+/).length > maxWords
    : false;

  useEffect(() => {
    if (!required) return;
    const hasError =
      ((required && String(value).trim() === "") || maxWordsCheck) &&
      !uncontrolledError;
    setError(checkError && hasError);
    if (onError) onError(hasError, id);
    setCheckError(true);
  }, [value, maxWordsCheck]);

  const applyClass =
    error || !!uncontrolledError
      ? styles.error
      : wasChanged
        ? approved
          ? styles.approved
          : dismiss
            ? styles.dismiss
            : changed
              ? styles.changed
              : styles.requireReview
        : approved
          ? styles.approved
          : changed
            ? styles.changed
            : "";

  return (
    <fieldset
      className={`max-w-full flex flex-col gap-2 ${
        error || !!uncontrolledError ? "error" : ""
      }`}
      key={id}
    >
      <div className="flex items-center justify-between">
        <label className="text-black text-base" htmlFor={id}>
          {label}
          {required ? <span className="text-[#cc092f]"> *</span> : null}
          {hasNote ? (
            <span className="text-[#97999b] text-sm"> {hasNote}</span>
          ) : null}
          {description ? <small> {description}</small> : null}
        </label>
        {!!approve && wasChanged && onChangeApprove ? (
          <ModerationButtons
            approved={approved || false}
            dismissed={dismiss || false}
            id={id}
            onChangeApprove={onChangeApprove}
          />
        ) : null}
      </div>
      <div
        className={cn(
          "relative h-[12rem] bg-white overflow-hidden",
          applyClass
        )}
      >
        {/* Not being able to set the height of quilleditor correctly, so applyClass needs to be on the parent component */}
        <QuillEditor
          className={cn(
            styles.editor,
            disabled ? "opacity-40 pointer-events-none" : ""
          )}
          formats={Formats}
          id={id}
          modules={Modules}
          onChange={(value: string) => {
            if (maxLength && countTextCharacters(value) > maxLength) return;
            onChange({ target: { id: id, value: value } });
          }}
          onKeyPress={(event) => {
            if (maxLength && countTextCharacters(value) >= maxLength) {
              event.preventDefault();
            }
          }}
          placeholder={placeholder}
          value={value}
        />
        {wasChanged && prevValue ? (
          <ChangesLog
            asHTML
            className="absolute top-[16px] right-[16px]"
            prevValue={prevValue}
            setShowLog={setShowLog}
            showLog={showLog}
          />
        ) : null}
      </div>
      {error && !uncontrolledError ? (
        <span>Required field</span>
      ) : !!uncontrolledError ? (
        <span className="relative mt-1 text-[16px]">{uncontrolledError}</span>
      ) : null}
    </fieldset>
  );
}
